import {
	Component,
	Ref,
	Vue,
	toNative,
} from 'vue-facing-decorator';
import { BModal } from 'bootstrap-vue';
import mitt from 'mitt';
import * as DB from 'interfaces/database';
import { TabEvent } from 'interfaces/app';
import Swal from 'sweetalert2';
import 'vue-loading-overlay/dist/css/index.css';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import LayoutService from 'services/LayoutService';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.LayoutModel>>();
@Component({
	components: {
		TabulatorBtn,
	},
	emits: ['openInNewTab', 'routeLayout'],
	mixins: [Template],
})
class LayoutTable extends Vue {
	@Ref('layout-modal')
	private readonly modal!: BModal;

	@Ref('layoutTable')
	private readonly tableReference!: HTMLDivElement;

	protected isLoading = false;

	private search = '';

	private id = '';

	private columnDefs: ColumnDefinitionExtended[] = [];

	private table?: Tabulator;

	private addLayoutData = {} as Pick<DB.LayoutModel, 'name'>;

	private isLoaded = false;

	private externalId = '';

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				title: 'id',
				field: 'id',
				headerFilter: true,
			},
			{
				title: 'Name',
				field: 'name',
				headerFilter: true,
			},
			{
				title: 'GroupId',
				field: 'groupid',
				headerFilter: true,
			},
			{
				title: 'TypeId',
				field: 'typeid',
				headerFilter: true,
			},
			{
				title: 'Icon',
				field: 'icon',
				formatter: 'image',
				formatterParams: {
					height: 200,
				},
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.LayoutModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deleteLayout,
		);
		eventBus.on(
			'editItem',
			this.editLayout,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount(): void {
		eventBus.off(
			'deleteItem',
			this.deleteLayout,
		);
		eventBus.off(
			'editItem',
			this.editLayout,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
				// create a context menu to open row on a new tab
				rowContextMenu: [
					{
						label: 'Open in new tab',
						action: (e, row) => {
							const data = row.getData();
							this.$emit(
								'openInNewTab',
								data.id,
							);
						},
					},
				],
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			const data = await LayoutService.getAll(parameter);
			this.table?.setData(data);
		} catch (err: any) {
			this.$toastError(err.message);
		} finally {
			this.table?.clearAlert();
		}
	}

	private editLayout(data: TabEvent<DB.LayoutModel>['editItem']): void {
		this.$emit(
			'routeLayout',
			data.id,
		);
	}

	private deleteLayout(data: TabEvent<DB.LayoutModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading..');
				// eslint-disable-next-line promise/no-nesting
				LayoutService.delete(data.id).then(() => {
					this.$toastSuccess('Item Deleted');
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$toastError(err.message);
		});
	}
}

export default toNative(LayoutTable);
